<template>
    <form-wrapper @form-submit="submit">
        <common-icon
            v-if="isSmartphoneLarge"
            class="offer-filter__close"
            name="close-black"
            size="medium"
            @click="emitEvent({ name: 'close-dropdown-form' })"
        ></common-icon>
        <form-row>
            <form-cell>
                <checkbox-input
                    :formData="data"
                    :value="data.includeDescription"
                    :label="$t('forms.offer.offer_filter.fields.include_description.label')"
                    valuePath="includeDescription"
                    @change="change"
                ></checkbox-input>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell width="half">
                <select-input
                    :formData="data"
                    :label="$t('forms.company.create_company.fields.country.label')"
                    :placeholder="$t('forms.company.create_company.fields.country.placeholder')"
                    :options="countryOptions"
                    :isNullable="true"
                    valuePath="countryId"
                    @change="countryChange"
                    @clear="clear"
                ></select-input>
            </form-cell>
            <form-cell width="half">
                <select-input
                    ref="citySelect"
                    :formData="data"
                    :label="$t('forms.offer.offer_filter.fields.city.label')"
                    :placeholder="$t('forms.offer.offer_filter.fields.city.placeholder')"
                    valuePath="cityId"
                    :options="cityOptions"
                    :isNullable="true"
                    :largeDropdown="true"
                    :isSearchable="true"
                    @change="change"
                    @clear="clearCity"
                ></select-input>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell width="half">
                <select-input
                    :formData="data"
                    :label="$t('forms.offer.offer_filter.fields.category.label')"
                    :placeholder="$t('forms.offer.offer_filter.fields.category.placeholder')"
                    :options="serviceCategories"
                    :isNullable="true"
                    :largeDropdown="true"
                    valuePath="serviceGroupId"
                    :isSearchable="true"
                    @change="categoryChange"
                    @clear="clearCategory"
                ></select-input>
            </form-cell>
            <form-cell width="half">
                <select-input
                    ref="selectSubCategories"
                    :formData="data"
                    :label="$t('forms.offer.offer_filter.fields.subcategory.label')"
                    :placeholder="$t('forms.offer.offer_filter.fields.subcategory.placeholder')"
                    :options="serviceSubCategories"
                    :isDisabled="!data.serviceGroupId"
                    :isNullable="true"
                    :largeDropdown="false"
                    valuePath="serviceId"
                    :isSearchable="true"
                    @change="change"
                    @clear="clearSubCategory"
                ></select-input>
            </form-cell>
        </form-row>

        <form-row class="offer-filter__form-row">
            <form-cell width="third">
                <select-input
                    :formData="data"
                    :label="$t('forms.offer.offer_filter.fields.currency.label')"
                    valuePath="currency"
                    :options="currencyOptions"
                    @change="change"
                ></select-input>
            </form-cell>
            <form-cell width="third">
                <text-input
                    :formData="data"
                    :error="priceFilterError"
                    :label="$t('forms.offer.offer_filter.fields.price_from.label')"
                    :placeholder="$t('forms.offer.offer_filter.fields.price_from.placeholder')"
                    valuePath="fromPrice"
                    type="number"
                    @change="change"
                ></text-input>
            </form-cell>
            <form-cell width="third">
                <text-input
                    :formData="data"
                    :label="$t('forms.offer.offer_filter.fields.price_to.label')"
                    :placeholder="$t('forms.offer.offer_filter.fields.price_to.placeholder')"
                    valuePath="toPrice"
                    type="number"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>

        <template #footer>
            <common-buttons>
                <common-button
                    v-if="isAnyFilterApplied"
                    :isProcessing="isLoading"
                    type="secondary"
                    :isSubmittable="false"
                    :label="$t('common.microcopy.reset')"
                    @click="emitEvent({ name: 'reset-filter' })"
                ></common-button>
                <common-button
                    :isDisabled="!!priceFilterError"
                    :isProcessing="isLoading"
                    type="primary"
                    :label="$t('common.microcopy.apply')"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import CurrencyEnum from 'enums/currency.enum'
    import LanguageEnum from 'enums/language.enum'
    import FormMixin from 'mixins/FormMixin'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import Store from '@/store'

    export default {
        mixins: [FormMixin, WindowResizeMixin],

        props: {
            prefill: { type: Object, default: undefined },
            countries: { type: Array, required: true },
            countryOptions: { type: Array, required: true },
            allCategories: { type: Array, required: true },
        },

        data() {
            return {
                selectedSubCategories: [],
                cityOptions: [],
                currencyOptions: Object.values(CurrencyEnum).map((currency) => ({ label: currency, value: currency })),
                allCountries: Store.getters['data/getCountries'],
            }
        },

        computed: {
            serviceCategories() {
                return this.allCategories.map((category) => ({
                    label:
                        this.$i18n.locale === LanguageEnum.EN
                            ? category.name
                            : category[`name${this.$i18n.locale.toUpperCase()}`],
                    value: category.id,
                }))
            },

            serviceSubCategories() {
                return this.selectedSubCategories?.map((subcategory) => ({
                    label:
                        this.$i18n.locale === LanguageEnum.EN
                            ? subcategory.name
                            : subcategory[`name${this.$i18n.locale.toUpperCase()}`],
                    value: subcategory.id,
                }))
            },

            priceFilterError() {
                if (this.data.fromPrice && this.data.toPrice && Number(this.data.fromPrice) > Number(this.data.toPrice)) {
                    return this.$t('forms.offer.offer_filter.price_missmatch')
                }
                return undefined
            },

            isAnyFilterApplied() {
                return Object.keys(this.data).filter((item) => item !== 'currency' && item !== 'errors').length > 0
            },
        },

        methods: {
            countryChange(event) {
                this.change(event)
                this.data.cityId = undefined
                this.$refs.citySelect.searchTerm = ''
                this.fetchCities(event.value)
            },

            clearCountry(event) {
                this.clear(event)
                this.data.cityId = undefined
                this.$refs.citySelect.searchTerm = ''
                this.cityOptions = []
            },

            fetchCities(selectedCountry) {
                this.cityOptions = this.countries
                    .find((country) => country.name === selectedCountry)
                    .cities.map((city) => ({
                        value: city.id,
                        label: city.name,
                    }))
            },

            categoryChange(event) {
                if (!event.value) {
                    return
                }
                this.data.serviceId = undefined
                this.$refs.selectSubCategories.searchTerm = ''
                this.change(event)
                this.loadSelectedSubCategories(event.value)
            },

            loadSelectedSubCategories(serviceGroupId) {
                this.selectedSubCategories = this.allCategories.find((category) => category.id === serviceGroupId)?.services
            },

            loadServiceSubCategories(serviceGroupId) {
                this.selectedSubCategories = this.allCategories.find((category) => category.id === serviceGroupId)?.services
            },

            clearCity(event) {
                this.clear(event)
                this.data.cityId = undefined
            },

            clearSubCategory(event) {
                this.clear(event)
                this.data.serviceId = undefined
            },

            clearCategory(event) {
                this.clear(event)
                this.data.serviceGroupId = undefined
                this.data.serviceId = undefined
            },

            prefillFilter() {
                const dataForPrefill = { currency: CurrencyEnum.EUR }
                if (this.prefill?.currency) {
                    dataForPrefill.currency = this.prefill.currency
                }
                if (this.prefill?.filter) {
                    dataForPrefill.filter = this.prefill.filter
                }
                if (this.prefill?.includeDescription && this.prefill.includeDescription != false) {
                    dataForPrefill.includeDescription = this.prefill.includeDescription
                }
                if (this.prefill?.fromPrice) {
                    dataForPrefill.fromPrice = Number(this.prefill.fromPrice)
                }
                if (this.prefill?.toPrice) {
                    dataForPrefill.toPrice = Number(this.prefill.toPrice)
                }
                if (this.prefill?.serviceGroupId) {
                    dataForPrefill.serviceGroupId = Number(this.prefill.serviceGroupId)
                    this.loadServiceSubCategories(dataForPrefill.serviceGroupId)
                }
                if (this.prefill?.serviceId) {
                    dataForPrefill.serviceId = Number(this.prefill.serviceId)
                }
                if (this.prefill?.countryId) {
                    const selectedCountry = this.allCountries.find((country) => country.id === this.prefill.countryId)
                    dataForPrefill.countryId = selectedCountry.name
                    this.fetchCities(selectedCountry.name)
                    if (this.prefill?.cityId) {
                        dataForPrefill.cityId = Number(this.prefill.cityId)
                    }
                }
                this.prefillData(dataForPrefill)
            },

            submit() {
                const query = {}
                if (this.data.countryId) {
                    const selectedCountry = this.allCountries.find((country) => country.name === this.data.countryId)
                    query.countryId = Number(selectedCountry.id)
                    if (this.data.cityId) {
                        query.cityId = this.data.cityId
                    }
                } else if (Store?.getters['data/getCountryByGeolocation']) {
                    query.countryId = Number(Store?.getters['data/getCountryByGeolocation'])
                }

                if (this.data.includeDescription) {
                    query.includeDescription = !!this.data?.includeDescription
                }
                if (this.data.fromPrice && this.data.fromPrice > 0) {
                    query.fromPrice = this.data.fromPrice
                }
                if (this.data.toPrice && this.data.toPrice > 0) {
                    query.toPrice = this.data.toPrice
                }
                if ((this.data.fromPrice && this.data.fromPrice > 0) || (this.data.toPrice && this.data.toPrice > 0)) {
                    query.currency = this.data.currency
                }
                if (this.data.serviceGroupId) {
                    query.serviceGroupId = this.data.serviceGroupId
                }
                if (this.data.serviceId) {
                    query.serviceId = this.data.serviceId
                }

                this.emitEvent({ name: 'filter-change', payload: query })
            },
        },

        mounted() {
            this.prefillFilter()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .form-row:first-child {
        margin-bottom: 25px;
    }
    .form-row:not(:first-child) {
        margin-top: 5px;
    }
    .page-plain {
        padding: 20px;
    }

    .row-hidden {
        display: none;
    }
    @include breakpoint(smartphone-large) {
        .form-cell--width-half:not(:first-child),
        .form-cell--width-third:not(:first-child) {
            margin-top: 5px;
        }
    }
</style>
